import * as angular from 'angular';

/**
 * Segmented radio input + string.
 *
 * - This component builds a segmented radio button list that has a `custom`
 *   value option. If `custom` is selected, it displays a text input field.
 * - This component is different from `radio-combo-input.component` in that only
 *   a single ng-model value is used. So when the `custom` option is chosen,
 *   the model is set to the value of the string field.
 */
class RadioPlusStringInputController implements angular.IController {
  private ngModelCtrl: angular.INgModelController;

  // bindings
  inputId: string;
  inputLabel: string;
  customInputLabel: string;
  radioOptions: { value: string | number; label: string; class?: string }[];
  ngModel: string | number; // exposed only to have this value available in init, otherwise manipulate with this.ngModelCtrl

  // Local state
  radiosName: string;
  customStringValue: string | number = '';
  optionValues: (string | number)[];
  radioState: string | number;

  constructor() {}

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.ngModel && !changes.ngModel.isFirstChange()) {
      this.updateUiBasedOnModel(changes.ngModel.currentValue);
    }
  }

  $onInit() {
    this.radiosName = `radio-combo-${this.inputId}`;
    this.optionValues = this.radioOptions.map((option) => option.value);

    // Set initial radio state based on the initial ngModel
    this.updateUiBasedOnModel(this.ngModel);
  }

  /**
   * Set model.
   *
   * This will trigger `on-change` on the component wrapper, if present.
   */
  setNgModel(value: string | number): void {
    this.ngModelCtrl.$setViewValue(value);
  }

  /**
   * Handle the radio changing event and set both radio state and component ngModel.
   */
  radiosChanged(value: string): void {
    this.setNgModel(value);
  }

  /**
   * Specifically handle the event when clicking the custom radio option
   */
  customRadioSelected(): void {
    this.setNgModel(this.customStringValue);
  }

  /**
   * Handle setting component ngModel in response to custom input change.
   */
  customInputChanged(): void {
    this.setNgModel(this.customStringValue);
  }

  /**
   * Update UI to ngModel value
   */
  updateUiBasedOnModel(modelValue: string | number) {
    if (this.optionValues.includes(modelValue)) {
      this.radioState = modelValue;
    } else {
      this.radioState = '';
      this.customStringValue = modelValue;
    }
  }

  shouldExposeCustomInput() {
    return !this.optionValues.includes(this.ngModelCtrl.$modelValue);
  }
}

export const RadioPlusStringInputComponent: angular.IComponentOptions = {
  bindings: {
    inputId: '@',
    inputLabel: '@',
    customInputLabel: '@',
    radioOptions: '<',
    ngModel: '<',
  },
  controller: RadioPlusStringInputController,
  controllerAs: 'ctrl',
  require: {
    ngModelCtrl: 'ngModel',
  },
  transclude: true,
  template: `
  <div class="radio-combo-field">
    <label ng-if="ctrl.inputLabel" class="field-label">{{ ctrl.inputLabel }}</label>
    <div class="radio-buttons radio-segmented">
      <label ng-repeat="radioOption in ctrl.radioOptions" class="radio-label">
        <input type="radio" ng-model="ctrl.radioState" ng-value="radioOption.value" ng-change="ctrl.radiosChanged(radioOption.value)" name="{{ ctrl.radiosName }}">
        <span class="radio-option-title">{{ radioOption.label }}</span>
      </label>

      <label class="radio-label radio-item-custom">
        <input type="radio" ng-model="ctrl.radioState" value="" ng-change="ctrl.customRadioSelected()" name="{{ ctrl.radiosName }}">
        <span class="radio-option-title">...</span>
      </label>
    </div>
    <div ng-if="ctrl.shouldExposeCustomInput()" class="custom-value-input combo-field">
      <label class="field-label">{{ ctrl.customInputLabel }}</label>
      <input type="text" class="form-control form-control-sm"
        ng-model="ctrl.customStringValue"
        ng-change="ctrl.customInputChanged()">
    </div>
  </div>
  `,
};
